import { FC, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserRoleContext } from '@components/Auth/UserRoleProvider';
import { Role } from '@app/enums/Role';
import {
  Button,
  CircularProgress,
  Container,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { signInPathFor } from '@utils/returnTo';
import { ToLogout } from '@app/navigation/ToLogout';

interface RoleGuardProps extends React.PropsWithChildren {
  requiredRoles: Role[];
}

export const RoleGuard: FC<RoleGuardProps> = ({ requiredRoles, children }) => {
  const { status, role, retry } = useContext(UserRoleContext);
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  const hasAccess = status === 'loaded' && requiredRoles.some((neededRole) => (role & neededRole) === neededRole);

  useEffect(() => {
    if (status === 'loggedOut') {
      navigate(signInPathFor(`${pathname}${search}${hash}`), { replace: true });
    } else if (status === 'loaded' && !hasAccess) {
      navigate('/teams', { replace: true });
    }
  }, [status, hasAccess, pathname, search, hash, navigate]);

  if (status === 'error') {
    return (
      <Container sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', height: '100vh' }}>
        <Paper sx={{ width: '100%', maxWidth: '500px' }}>
          <DialogTitle>Something&#39;s Not Quite Right</DialogTitle>
          <DialogContent>
            <Typography>
              We couldn&#39;t load your account. Please check your connection and try again. If that doesn&#39;t help,
              sign out and sign back in.
            </Typography>
          </DialogContent>
          <DialogActions disableSpacing sx={{ flexDirection: 'column', gap: 1, paddingBottom: '25px' }}>
            <Button variant={'contained'} onClick={retry} sx={{ width: '80%' }}>
              Try Again
            </Button>
            <Button onClick={ToLogout} sx={{ width: '80%' }}>
              Sign Out
            </Button>
          </DialogActions>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      {/** Loading State */}
      {(status === 'loading' || status === 'loggedOut') && (
        <Grid
          container
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Grid>
      )}
      {/** Allowed Content */}
      {hasAccess && children}
    </>
  );
};
